import { call, put, select } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { t } from "i18next";
import { IApiError, AlertStatusMessage } from "@/shared/types";
import { commonSlice } from "@/entities/common";
import { alertsSagaActions } from "@/entities/alerts";

import { mrnCategoriesSlice } from "../model/slice";
import {
  sendGetMrnCategoryRequest,
  sendCreateMrnCategoryRequest,
  sendUpdateMrnCategoryRequest,
  sendDeleteMrnCategoryRequest,
  sendGetManyMrnCategoriesRequest,
} from "./senders";
import {
  GetMrnCategoryPayload,
  GetMrnCategoryResponse,
  CreateMrnCategoryPayload,
  UpdateMrnCategoryPayload,
  DeleteMrnCategoryPayload,
  GetManyMrnCategoriesPayload,
  GetManyMrnCategoriesResponse,
} from "./types/types";

export function* handleCreateMrnCategory(action: PayloadAction<CreateMrnCategoryPayload>): Generator<any, void, any> {
  const { formData } = action.payload;
  const { updateAlert } = alertsSagaActions;

  try {
    yield call(sendCreateMrnCategoryRequest, formData);
    yield put(updateAlert({ status: AlertStatusMessage.SUCCESS, message: `${t("mrn_category_created_successfully")}` }));
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield call(handleRetrieveManyMrnCategories);
  }
}

export function* handleUpdateMrnCategory(action: PayloadAction<UpdateMrnCategoryPayload>): Generator<any, void, any> {
  const { mrnCategoryId, formData } = action.payload;
  const { updateAlert } = alertsSagaActions;

  try {
    yield call(sendUpdateMrnCategoryRequest, mrnCategoryId, formData);
    yield put(updateAlert({ status: AlertStatusMessage.SUCCESS, message: `${t("mrn_category_updated_successfully")}` }));
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield call(handleRetrieveManyMrnCategories);
  }
}

export function* handleDeleteMrnCategory(action: PayloadAction<DeleteMrnCategoryPayload>): Generator<any, void, any> {
  const { mrnCategoryId } = action.payload;
  const { updateAlert } = alertsSagaActions;

  try {
    yield call(sendDeleteMrnCategoryRequest, mrnCategoryId);
    yield put(updateAlert({ status: AlertStatusMessage.SUCCESS, message: `${t("mrn_category_deleted_successfully")}` }));
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield call(handleRetrieveManyMrnCategories);
  }
}

export function* handleRetrieveMrnCategory(action: PayloadAction<GetMrnCategoryPayload>): Generator<any, void, any> {
  const { mrnCategoryId } = action.payload;

  try {
    const response: GetMrnCategoryResponse = yield call(sendGetMrnCategoryRequest, mrnCategoryId);
    const mrnCategory: any = response.data.data;
    yield put(commonSlice.setActiveEntity({ activeEntity: mrnCategory }));
  } catch (err) {
    yield call(handleError, err);
  }
}

export function* handleRetrieveManyMrnCategories(action?: PayloadAction<GetManyMrnCategoriesPayload>): Generator<any, void, any> {
  const { setMrnCategories, setParams } = mrnCategoriesSlice;

  if (action?.payload?.params) {
    yield put(setParams({ params: action.payload.params }));
  }

  try {
    const bodyParams: any = (yield select((state: any) => state.mrnCategories.params)) as any;
    const response: GetManyMrnCategoriesResponse = yield call(sendGetManyMrnCategoriesRequest, bodyParams);
    const { items, count } = response.data.data;
    yield put(setMrnCategories({ data: { items, count } }));
  } catch (err) {
    yield call(handleError, err);
  }
}

function* handleError(err: any) {
  const { updateAlert } = alertsSagaActions;
  const error = err as IApiError;

  if (error?.response?.data?.error?.message?.length) {
    for (const text of error.response.data.error.message) {
      yield put(updateAlert({ status: AlertStatusMessage.ERROR, message: text }));
    }
  }
}
